import { Modal } from './Modal'
import { getAudioManager, getVolumeCeilings, getSettingsDefaults } from '../features/audio'
import { setRainAudioVolume } from '../features/rain/global-rain-manager'
import { applyAnimationSettings, loadAnimationsEnabled } from '../utils/animation-settings'
import track, { CHANGE_SETTINGS } from '../features/analytics'
import iconMusicSvg from '../svg/icon-music.svg?raw'
import iconSpeechSvg from '../svg/icon-speech.svg?raw'
import iconSoundSvg from '../svg/icon-sound.svg?raw'
import iconAnimationSvg from '../svg/icon-animation.svg?raw'
import iconInfoSvg from '../svg/icon-info.svg?raw'

export interface SoundSettings {
  music: number
  speech: number
  sound: number
}

export interface AppSettings {
  sound: SoundSettings
  animationsEnabled: boolean
}

const STORAGE_KEY = 'rychle-sipy-a-velikonocni-poklad:settings'

function getDefaultSettings(): AppSettings {
  const d = getSettingsDefaults()
  return {
    sound: { music: d.music, speech: d.speech, sound: d.sound },
    animationsEnabled: loadAnimationsEnabled(),
  }
}

function applySpeechVolume(volume: number): void {
  getAudioManager().setSpeechVolume(volume)
}

function applySoundVolume(volume: number): void {
  const ceilings = getVolumeCeilings()
  const normalized = (Math.max(0, Math.min(100, volume)) / 100) * ceilings.sound
  getAudioManager().setSoundVolume(volume)
  setRainAudioVolume(normalized)
  const state = (window as any).__appState as { sfxManager?: { setVolume: (v: number) => void }; scrollSfxManager?: { setVolume: (v: number) => void } } | undefined
  state?.sfxManager?.setVolume(normalized)
  state?.scrollSfxManager?.setVolume(normalized)
}

function applyMusicVolume(volume: number): void {
  const ceilings = getVolumeCeilings()
  const state = (window as any).__appState as { atmoManager?: { setVolume: (v: number) => void }; musicManager?: { setVolume: (v: number) => void } } | undefined
  const userScale = Math.max(0, Math.min(100, volume)) / 100
  state?.musicManager?.setVolume(userScale * ceilings.music)
  state?.atmoManager?.setVolume(userScale * ceilings.atmo)
}

/** Settings state + render descriptor only. Uses shared Modal for all UI. */
export class SettingsModal {
  private settings: AppSettings
  private trackTimers: Map<string, ReturnType<typeof setTimeout>> = new Map()

  constructor(private modal: Modal) {
    this.settings = this.loadSettings()
  }

  private trackSettingDebounced(name: string, value: string): void {
    const existing = this.trackTimers.get(name)
    if (existing) clearTimeout(existing)
    this.trackTimers.set(
      name,
      setTimeout(() => {
        track(CHANGE_SETTINGS, name, value)
        this.trackTimers.delete(name)
      }, 500),
    )
  }

  show(): void {
    this.modal.show((dialog) => this.renderInto(dialog), { lenisPrevent: true })
  }

  hide(): void {
    this.modal.hide()
  }

  getSettings(): AppSettings {
    return { ...this.settings }
  }

  private loadSettings(): AppSettings {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as AppSettings & { sound?: SoundSettings & { ambient?: number } }
        if (parsed.sound) {
          if (parsed.sound.sound === undefined && parsed.sound.ambient !== undefined) {
            parsed.sound.sound = parsed.sound.ambient
          }
          delete (parsed.sound as unknown as Record<string, unknown>).ambient
          if (parsed.sound.speech !== undefined) applySpeechVolume(parsed.sound.speech)
          if (parsed.sound.sound !== undefined) applySoundVolume(parsed.sound.sound)
          if (parsed.sound.music !== undefined) applyMusicVolume(parsed.sound.music)
        }
        if (parsed.animationsEnabled === undefined) {
          parsed.animationsEnabled = loadAnimationsEnabled()
        }
        applyAnimationSettings(parsed.animationsEnabled)
        return parsed as AppSettings
      } catch {
        // fallback
      }
    }
    const defaults = getDefaultSettings()
    applySpeechVolume(defaults.sound.speech)
    applySoundVolume(defaults.sound.sound)
    applyMusicVolume(defaults.sound.music)
    applyAnimationSettings(defaults.animationsEnabled)
    return defaults
  }

  private saveSettings(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings))
  }

  private resetSound(): void {
    const defaults = getDefaultSettings()
    this.settings.sound = { ...defaults.sound }
    this.saveSettings()
    applySpeechVolume(this.settings.sound.speech)
    applySoundVolume(this.settings.sound.sound)
    applyMusicVolume(this.settings.sound.music)
    this.modal.updateContent((dialog) => this.renderInto(dialog))
    track(CHANGE_SETTINGS, 'reset')
  }

  private renderInto(dialog: HTMLDivElement): void {
    dialog.appendChild(Modal.createHeader('Nastavení', () => this.modal.hide()))
    const content = Modal.createContent()

    const soundSection = Modal.createSection('Hlasitost zvuku', { onReset: () => this.resetSound() })
    soundSection.appendChild(
      Modal.createSliderControl({
        label: 'Hudba',
        iconSvg: iconMusicSvg,
        value: this.settings.sound.music,
        onChange: (v) => {
          this.settings.sound.music = v
          this.saveSettings()
          applyMusicVolume(v)
          this.trackSettingDebounced('Hudba', String(v))
        },
      }),
    )
    soundSection.appendChild(
      Modal.createSliderControl({
        label: 'Řeč',
        iconSvg: iconSpeechSvg,
        value: this.settings.sound.speech,
        onChange: (v) => {
          this.settings.sound.speech = v
          this.saveSettings()
          applySpeechVolume(v)
          this.trackSettingDebounced('Řeč', String(v))
        },
      }),
    )
    soundSection.appendChild(
      Modal.createSliderControl({
        label: 'Zvuky',
        iconSvg: iconSoundSvg,
        value: this.settings.sound.sound,
        onChange: (v) => {
          this.settings.sound.sound = v
          this.saveSettings()
          applySoundVolume(v)
          this.trackSettingDebounced('Zvuky', String(v))
        },
      }),
    )
    content.appendChild(soundSection)

    const accessibilitySection = Modal.createSectionTitleOnly('Přístupnost')
    const animHint = document.createElement('p')
    animHint.className = 'modal__hint'
    const hintIcon = document.createElement('span')
    hintIcon.className = 'modal__hint-icon'
    hintIcon.setAttribute('aria-hidden', 'true')
    hintIcon.innerHTML = iconInfoSvg
    const hintText = document.createElement('span')
    hintText.className = 'modal__hint-text'
    hintText.textContent = 'Po změně tohoto nastavení obnovte stránku pro správné fungování.'
    animHint.appendChild(hintIcon)
    animHint.appendChild(hintText)
    accessibilitySection.appendChild(animHint)
    accessibilitySection.appendChild(
      Modal.createToggleControl({
        label: 'Animace',
        iconSvg: iconAnimationSvg,
        checked: this.settings.animationsEnabled,
        onChange: (v) => {
          this.settings.animationsEnabled = v
          this.saveSettings()
          applyAnimationSettings(v)
          track(CHANGE_SETTINGS, 'Animace', v ? 'on' : 'off')
        },
        ariaLabel: 'Přepnout animace',
      }),
    )
    content.appendChild(accessibilitySection)

    dialog.appendChild(content)
  }
}
